import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import Seo from '../components/Seo';
import { useSubscription } from '../useSubscription';
import './AccountPage.css';

export default function AccountPage() {
  const { session, signOut, updatePassword } = useAuth();
  const { isPro, subData } = useSubscription();
  const navigate = useNavigate();

  const [signingOut,         setSigningOut]         = useState(false);
  const [showCancelModal,    setShowCancelModal]    = useState(false);
  const [cancelLoading,      setCancelLoading]      = useState(false);
  const [cancelError,        setCancelError]        = useState(null);
  const [cancelledAt,        setCancelledAt]        = useState(null);
  const [reactivateLoading,  setReactivateLoading]  = useState(false);
  const [reactivateError,    setReactivateError]    = useState(null);
  const [reactivated,        setReactivated]        = useState(false);
  const [showDeleteModal,    setShowDeleteModal]    = useState(false);
  const [deleteLoading,      setDeleteLoading]      = useState(false);
  const [deleteError,        setDeleteError]        = useState(null);

  // Password reset flow — triggered when user arrives via reset email link
  const [isRecovery,   setIsRecovery]   = useState(false);
  const [newPassword,  setNewPassword]  = useState('');
  const [pwLoading,    setPwLoading]    = useState(false);
  const [pwError,      setPwError]      = useState(null);
  const [pwSuccess,    setPwSuccess]    = useState(false);

  // Detect PASSWORD_RECOVERY auth event from Supabase
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      setIsRecovery(true);
    }
  }, []);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setPwError('Password must be at least 6 characters.');
      return;
    }
    setPwLoading(true);
    setPwError(null);
    const { error } = await updatePassword(newPassword);
    if (error) {
      setPwError(error.message);
    } else {
      setPwSuccess(true);
      setIsRecovery(false);
      window.history.replaceState(null, '', '/account');
    }
    setPwLoading(false);
  };

  const handleReactivate = async () => {
    setReactivateLoading(true);
    setReactivateError(null);
    try {
      const res  = await fetch('/api/reactivate-subscription.php', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        setReactivated(true);
      } else {
        setReactivateError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setReactivateError('Could not connect. Please try again.');
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const res  = await fetch('/api/delete-account.php', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        await signOut();
        navigate('/');
      } else {
        setDeleteError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setDeleteError('Could not connect. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    setCancelError(null);
    try {
      const res = await fetch('/api/cancel-subscription.php', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + session.access_token,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (data.success) {
        setShowCancelModal(false);
        setCancelledAt(subData?.periodEnd ?? null);
      } else {
        setCancelError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setCancelError('Could not connect. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  // Redirect to app if not logged in
  useEffect(() => {
    if (session === null) navigate('/app');
  }, [session, navigate]);

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate('/');
  };

  if (session === undefined) return null;

  const email = session?.user?.email ?? '';

  return (
    <div className="account-page">
      <Seo path="/account" title="Your Account — LottoPickle" noindex />
      <nav className="account-nav">
        <Link to="/" className="account-nav-logo">Lotto<span>Pickle</span></Link>
        <Link to="/app" className="account-nav-back">&larr; Back to app</Link>
      </nav>

      <div className="account-content">
        <h1>Account</h1>

        {/* Password reset success banner */}
        {pwSuccess && (
          <div className="account-banner-success">
            Password updated successfully.
          </div>
        )}

        {/* Set new password (recovery flow) */}
        {isRecovery && (
          <section className="account-section account-section-recovery">
            <h2>Set new password</h2>
            <p className="account-manage-note">Choose a new password for your account.</p>
            <form onSubmit={handleUpdatePassword} style={{ marginTop: '12px' }}>
              <input
                className="account-input"
                type="password"
                placeholder="New password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={6}
                required
                autoFocus
              />
              {pwError && <p className="account-portal-error">{pwError}</p>}
              <button
                className="account-btn account-btn-primary"
                type="submit"
                disabled={pwLoading}
                style={{ marginTop: '12px' }}
              >
                {pwLoading ? 'Saving…' : 'Update password'}
              </button>
            </form>
          </section>
        )}

        {/* Profile */}
        <section className="account-section">
          <h2>Profile</h2>
          <div className="account-row">
            <span className="account-label">Email</span>
            <span className="account-value">{email}</span>
          </div>
        </section>

        {/* Subscription */}
        <section className="account-section">
          <h2>Subscription</h2>
          <div className="account-row">
            <span className="account-label">Status</span>
            <span className={`account-badge ${isPro ? 'badge-premium' : 'badge-free'}`}>
              {isPro ? '⭐ Premium' : 'Free'}
            </span>
          </div>
          {isPro && subData?.plan && (
            <div className="account-row">
              <span className="account-label">Plan</span>
              <span className="account-value" style={{ textTransform: 'capitalize' }}>{subData.plan}</span>
            </div>
          )}
          {isPro && subData?.periodEnd && (
            <div className="account-row">
              <span className="account-label">{subData.cancelAtPeriodEnd ? 'Access until' : 'Renews'}</span>
              <span className="account-value">
                {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(subData.periodEnd.replace(' ', 'T') + 'Z'))}
              </span>
            </div>
          )}

          {isPro ? (
            <div className="account-manage">
              {cancelledAt ? (
                <p className="account-manage-note">
                  Your subscription has been cancelled. You'll keep Premium access until{' '}
                  <strong>{new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(cancelledAt))}</strong>.
                </p>
              ) : (subData?.cancelAtPeriodEnd && !reactivated) ? (
                <>
                  <p className="account-manage-note">
                    Cancels at end of billing period. You keep Premium access until{' '}
                    <strong>{new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(subData.periodEnd.replace(' ', 'T') + 'Z'))}</strong>.
                  </p>
                  {reactivateError && <p className="account-portal-error">{reactivateError}</p>}
                  <button
                    className="account-btn account-btn-primary"
                    onClick={handleReactivate}
                    disabled={reactivateLoading}
                  >
                    {reactivateLoading ? 'Reactivating…' : 'Reactivate subscription'}
                  </button>
                </>
              ) : reactivated ? (
                <p className="account-manage-note">
                  Your subscription has been reactivated and will renew as normal.
                </p>
              ) : (
                <button
                  className="account-btn account-btn-secondary"
                  onClick={() => setShowCancelModal(true)}
                >
                  Cancel subscription
                </button>
              )}
            </div>
          ) : (
            <div className="account-manage">
              <p className="account-manage-note">
                Upgrade to Premium to unlock Trends, Distribution, Number Pairs, Gap Analysis, and advanced picker modes.
              </p>
              <Link className="account-btn account-btn-primary" to="/pricing">
                View pricing
              </Link>
            </div>
          )}
        </section>

        {/* Sign out */}
        <section className="account-section">
          <h2>Session</h2>
          <button
            className="account-btn account-btn-danger"
            onClick={handleSignOut}
            disabled={signingOut}
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </section>

        {/* Delete account */}
        <section className="account-section">
          <h2>Delete account</h2>
          <p className="account-manage-note">
            Permanently deletes your account data and cancels any active subscription.
            This cannot be undone.
          </p>
          <button
            className="account-btn account-btn-danger"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete my account
          </button>
        </section>
      </div>

      {/* Cancel confirmation modal */}
      {showCancelModal && (
        <div className="account-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowCancelModal(false)}>
          <div className="account-modal">
            <h2>Cancel subscription?</h2>
            <p>
              You'll keep Premium access until{' '}
              <strong>
                {subData?.periodEnd
                  ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(subData.periodEnd.replace(' ', 'T') + 'Z'))
                  : 'the end of your billing period'}
              </strong>.
              After that, your account will revert to the free plan.
            </p>
            {cancelError && <p className="account-portal-error">{cancelError}</p>}
            <div className="account-modal-actions">
              <button
                className="account-btn account-btn-danger"
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
              >
                {cancelLoading ? 'Cancelling…' : 'Yes, cancel'}
              </button>
              <button
                className="account-btn account-btn-secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={cancelLoading}
              >
                Keep Premium
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete account confirmation modal */}
      {showDeleteModal && (
        <div className="account-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowDeleteModal(false)}>
          <div className="account-modal">
            <h2>Delete your account?</h2>
            <p>
              This will permanently delete your account data and cancel any active subscription immediately.
              You will lose Premium access right away. <strong>This cannot be undone.</strong>
            </p>
            {deleteError && <p className="account-portal-error">{deleteError}</p>}
            <div className="account-modal-actions">
              <button
                className="account-btn account-btn-danger"
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting…' : 'Yes, delete my account'}
              </button>
              <button
                className="account-btn account-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="account-footer">
        <ul className="account-footer-links">
          <li><Link to="/terms">Terms</Link></li>
          <li><Link to="/privacy">Privacy</Link></li>
          <li><Link to="/refunds">Refunds</Link></li>
          <li><Link to="/disclaimer">Disclaimer</Link></li>
          <li><a href="mailto:support@lottopickle.com">Support</a></li>
        </ul>
      </footer>
    </div>
  );
}
